import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Shared Help & Glossary chrome (handoff 12 §A; handoff 30 Items 2–3).
//
// Before this, the Help drawer + its "?" trigger only existed on Analytics and
// Disclosure, each mounting its own <HelpDrawer> with local open state. Dev
// review asked for the drawer to be a top-bar fixture on EVERY page. So the
// single source of truth for "is help open" and "what page context to show"
// now lives here, one provider mounted in Layout, one <HelpDrawer> instance.
//
//   - openHelp() / closeHelp() / open   : the shared open state (the "?" in
//     DashTopNav and any page-local trigger both call openHelp).
//   - lockHelp() / unlockHelp() / locked : product-tour glossary steps keep
//     the drawer open (ignore backdrop / Escape) until the spotlight is done.
//   - openHelpToTerm(slug)              : open drawer and scroll to #gl-<slug>
//   - contextNote / referenceBlock      : the CURRENT page's help context.
//   - siteContext                       : personalised score primer values
//     (band, brittleness, processes) for ◄ row marking (handoff 30 Item 2).

const HelpContext = createContext({
  open: false,
  locked: false,
  openHelp: () => {},
  closeHelp: () => {},
  lockHelp: () => {},
  unlockHelp: () => {},
  openHelpToTerm: () => {},
  contextNote: undefined,
  referenceBlock: undefined,
  siteContext: undefined,
  focusTermId: null,
  clearFocusTerm: () => {},
  setPageHelp: () => {},
});

export function HelpProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [locked, setLocked] = useState(false);
  const [focusTermId, setFocusTermId] = useState(null);
  const [pageHelp, setPageHelp] = useState({
    contextNote: undefined,
    referenceBlock: undefined,
    siteContext: undefined,
  });

  const openHelp = useCallback(() => setOpen(true), []);
  const closeHelp = useCallback(() => {
    setOpen(false);
    setFocusTermId(null);
  }, []);
  const lockHelp = useCallback(() => setLocked(true), []);
  const unlockHelp = useCallback(() => setLocked(false), []);
  const clearFocusTerm = useCallback(() => setFocusTermId(null), []);
  const openHelpToTerm = useCallback((slug) => {
    if (!slug) return;
    setFocusTermId(slug);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({
    open,
    locked,
    openHelp,
    closeHelp,
    lockHelp,
    unlockHelp,
    openHelpToTerm,
    contextNote: pageHelp.contextNote,
    referenceBlock: pageHelp.referenceBlock,
    siteContext: pageHelp.siteContext,
    focusTermId,
    clearFocusTerm,
    setPageHelp,
  }), [open, locked, openHelp, closeHelp, lockHelp, unlockHelp, openHelpToTerm, pageHelp, focusTermId, clearFocusTerm]);

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp() {
  return useContext(HelpContext);
}

/**
 * Register the current page's help context with the shared drawer.
 *
 * @param {{ contextNote?: string, referenceBlock?: React.ReactNode, siteContext?: object }} cfg
 */
export function usePageHelp({ contextNote, referenceBlock, siteContext } = {}) {
  const { setPageHelp } = useHelp();
  useEffect(() => {
    setPageHelp({ contextNote, referenceBlock, siteContext });
    return () => setPageHelp({
      contextNote: undefined,
      referenceBlock: undefined,
      siteContext: undefined,
    });
  }, [contextNote, referenceBlock, siteContext, setPageHelp]);
}
